// trade-planet-admin-v2/src/stores/main.js

import { defineStore } from 'pinia'
import { ref } from 'vue'
import axios from 'axios'

export const useMainStore = defineStore("main", () => {

  // Admin user session/auth data
  const userName = ref("");
  const userEmail = ref("");
  const userRole = ref("");
  const userPermissions = ref([]);
  const adminTotalItemEdits = ref(0);
  const adminTotalUserEdits = ref(0);
  const staffOverview = ref([]);
  const error = ref(null);
  const showWelcomeOnNextDashboard = ref(false);

  // Whether we've resolved auth state at least once this session (true
  // whether that check succeeded or came back "not logged in"). Lets
  // ensureUser() below skip re-probing the API on every navigation.
  const authChecked = ref(false);
  let fetchUserPromise = null;

  const API_BASE_URL = process.env.VUE_APP_API_BASE_URL;

  // The auth token lives in an httpOnly cookie set by the API — it's never
  // readable from JS here. Every request just relies on the browser sending
  // it automatically (see axios.defaults.withCredentials in main.js).
  // Always hits the API — call this after login/logout when you need a
  // guaranteed-fresh read. For "am I logged in?" checks, use ensureUser().
  async function fetchUser() {
    try {
      const response = await axios.get(`${API_BASE_URL}/v2/auth/me`);

      if (response.data) {
        userName.value = response.data.username;
        userEmail.value = response.data.email;
        userRole.value = response.data.role;
        userPermissions.value = response.data.permissions || [];
        adminTotalItemEdits.value = response.data.adminTotalItemEdits || 0;
        adminTotalUserEdits.value = response.data.adminTotalUserEdits || 0;
      } else {
        console.warn("Unexpected response structure:", response.data);
      }
    } catch (err) {
      console.error("Error fetching user data:", err.response?.data || err.message);
      throw err;
    } finally {
      authChecked.value = true;
    }
  }

  // Resolves auth state at most once per session, and de-dupes concurrent
  // callers (e.g. rapid navigations) onto a single in-flight request instead
  // of each firing their own. This is what route guards should call.
  function ensureUser() {
    if (authChecked.value) return Promise.resolve();

    if (!fetchUserPromise) {
      fetchUserPromise = fetchUser().finally(() => {
        fetchUserPromise = null;
      });
    }

    return fetchUserPromise;
  }

  async function handleLogout() {
    try {
      await axios.post(`${API_BASE_URL}/v2/auth/logout`);
    } catch (err) {
      console.error("Logout API Error:", err.response?.data || err.message);
    }

    userName.value = "";
    userEmail.value = "";
    userRole.value = "";
    userPermissions.value = [];
    adminTotalItemEdits.value = 0;
    adminTotalUserEdits.value = 0;
    showWelcomeOnNextDashboard.value = false;
    authChecked.value = false;

    const mainSiteUrl = process.env.VUE_APP_MAIN_SITE_URL || "https://trade-planet.net";
    window.location.href = mainSiteUrl;
  }

  function setupAxiosInterceptors() {
    axios.interceptors.response.use(
      (response) => response,
      (err) => {
        // Only treat this as a session expiring if we were actually logged
        // in — a 401 from the initial "am I logged in?" probe just means
        // "not logged in," not "your session died," and shouldn't redirect
        // someone who's simply visiting the login page.
        if (err.response && err.response.status === 401 && userName.value) {
          console.warn('Session expired! Logging out...')
          handleLogout()
        }
        return Promise.reject(err)
      }
    )
  }

  // Local profile update (e.g. from Settings form)
  function setUser(payload) {
    if (payload.name !== undefined) userName.value = payload.name
    if (payload.email !== undefined) userEmail.value = payload.email
  }

  // Sample/demo data (for dashboard etc.)
  const isFieldFocusRegistered = ref(false)
  const clients = ref([])
  const history = ref([])
  const products = ref([])
  const updates = ref([])

  async function fetchStaffOverview() {
    try {
      const response = await axios.get(`${API_BASE_URL}/v2/admin/staff-overview`)
      staffOverview.value = Array.isArray(response.data) ? response.data : []
    } catch (err) {
      console.error('Error fetching staff overview:', err.response?.data || err.message)
      staffOverview.value = []
    }
  }

  /*

  function fetchSampleClients() {
    axios
      .get('data-sources/clients.json')
      .then((result) => {
        clients.value = result?.data?.data ?? []
      })
      .catch((err) => {
        console.error(err.message)
      })
  }

  function fetchSampleHistory() {
    axios
      .get('data-sources/history.json')
      .then((result) => {
        history.value = result?.data?.data ?? []
      })
      .catch((err) => {
        console.error(err.message)
      })
  }

  function fetchSampleProducts() {
    axios
      .get('data-sources/products.json')
      .then((result) => {
        products.value = result?.data?.data ?? []
      })
      .catch((err) => {
        console.error(err.message)
      })
  }

  function fetchSampleUpdates() {
    axios
      .get('data-sources/updates.json')
      .then((result) => {
        updates.value = result?.data?.data ?? []
      })
      .catch((err) => {
        console.error(err.message)
      })
  }

  */

  return {
    userName,
    userEmail,
    userRole,
    userPermissions,
    adminTotalItemEdits,
    adminTotalUserEdits,
    staffOverview,
    error,
    showWelcomeOnNextDashboard,
    fetchUser,
    ensureUser,
    handleLogout,
    setupAxiosInterceptors,
    setUser,
    isFieldFocusRegistered,
    clients,
    history,
    products,
    updates,
    fetchStaffOverview,
//    fetchSampleClients,
//    fetchSampleHistory,
//    fetchSampleProducts,
//    fetchSampleUpdates
  }
})
